import * as React from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  MdBusiness,
  MdCalendarToday,
  MdCheckCircle,
  MdEdit,
  MdEmail,
  MdListAlt,
  MdNotificationsActive,
  MdPerson,
  MdPhone,
  MdSchedule,
  MdWarningAmber,
  MdMonitor,
} from 'react-icons/md';
import { toast } from 'react-toastify';
import { useAuth } from '../../Context/AuthContext';
import { PermissionCodes, permissionHas, resolveUserPermissionCodes } from '../../Utils/Permissions';
import { APPRoutes } from '../../Utils/Route';
import { formatUtcToLocalDateTime } from '../../Utils/DateTime';
import { getTrackingById, getAllTrackings } from '../../Services/TrackingService';
import ViewEmptyState from '../../Components/Common/ViewEmptyState';
import BackButton from '../../Components/Common/BackButton';

const STATUS_STYLE = {
  Active: { backgroundColor: 'var(--hc-status-success-bg)', color: 'var(--hc-status-success-text-strong)' },
  'In-Progress': { backgroundColor: 'var(--hc-blue-100)', color: 'var(--hc-blue-700)' },
  Pending: { backgroundColor: 'var(--hc-status-pending-bg)', color: 'var(--hc-status-pending-text)' },
  Completed: { backgroundColor: 'var(--hc-status-success-bg)', color: 'var(--hc-status-success-text-strong)' },
  Overdue: { backgroundColor: 'var(--hc-status-danger-bg)', color: 'var(--hc-status-danger-text)' },
  Unknown: { backgroundColor: 'var(--hc-status-neutral-bg)', color: 'var(--hc-table-footer-text)' },
};

const formatDisplayName = (provider) => {
  const parts = [provider?.first_name, provider?.middle_name, provider?.last_name].filter(Boolean);
  return parts.length ? parts.join(' ') : 'Unknown Provider';
};

const formatValue = (value, fallback = '-') => {
  if (value === null || value === undefined || value === '') {
    return fallback;
  }

  return value;
};

const formatDate = (value) => {
  if (!value) {
    return '-';
  }

  return formatUtcToLocalDateTime(value);
};

const normalizeTrackingPayload = (payload) => {
  if (!payload) {
    return null;
  }

  const rawGroup = payload?._raw?.group;
  const input = rawGroup ?? payload;
  const item = Array.isArray(input?.data) ? input.data[0] : input?.data ?? input;
  const provider = item?.provider || item?.Provider || input?.provider || input?.Provider || payload?.provider || {};
  const payor = item?.healthcare_payor || item?.HealthcarePayor || item?.healthcarePayor || item?.payor || input?.healthcare_payor || input?.HealthCarePayor || input?.healthcarePayor || input?.payor || payload?.payor || {};
  const tracks = Array.isArray(item?.tracks)
    ? item.tracks
    : Array.isArray(item?.Trackings)
      ? item.Trackings
      : Array.isArray(item?.trackings)
        ? item.trackings
        : Array.isArray(input?.tracks)
          ? input.tracks
          : Array.isArray(input?.Trackings)
            ? input.Trackings
            : [];

  const fallbackTrack = tracks[0] || null;
  const providerId = fallbackTrack?.provider_id ?? fallbackTrack?.ProviderId ?? item?.provider_id ?? item?.ProviderId ?? payload?.provider_id ?? provider?.id ?? provider?.Id ?? null;
  const payorId = fallbackTrack?.healthcare_payor_id ?? fallbackTrack?.HealthcarePayorId ?? fallbackTrack?.healthcarePayorId ?? item?.healthcare_payor_id ?? item?.HealthcarePayorId ?? payload?.healthcare_payor_id ?? payor?.id ?? payor?.Id ?? item?.payor_id ?? item?.PayorId ?? null;

  return {
    provider,
    payor,
    tracks,
    providerId,
    payorId,
    totalTrackings: payload?.total_trackings ?? item?.total_trackings ?? tracks.length,
    overdueTrackings: payload?.overdue_trackings ?? item?.overdue_trackings ?? tracks.filter((track) => Number(track?.overdue_days || 0) > 0).length,
    providerName: payload?.ProviderName ?? payload?.providerName ?? formatDisplayName(provider),
    payorName: payload?.PayorName ?? payload?.payorName ?? payor?.payor_name ?? payor?.PayorName ?? 'Unknown Payor',
    statusName: fallbackTrack?.status_name ?? fallbackTrack?.StatusName ?? payload?.StatusName ?? payload?.statusName ?? 'Unknown',
    managedByName: fallbackTrack?.created_by_name ?? fallbackTrack?.createdByName ?? fallbackTrack?.created_by ?? payload?.ManagedBy ?? null,
    createdOn: fallbackTrack?.created_on ?? fallbackTrack?.CreatedOn ?? null,
  };
};

export default function TrackingView() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const { token, permissions, decodedToken } = useAuth();

  const userPermissionCodes = React.useMemo(
    () => resolveUserPermissionCodes({ permissions, decodedToken }),
    [permissions, decodedToken],
  );

  const canViewTracking = permissionHas(userPermissionCodes, PermissionCodes.CREDENTIALING_TRACKING_VIEW, decodedToken);
  const canEditTracking = permissionHas(userPermissionCodes, PermissionCodes.CREDENTIALING_TRACKING_EDIT, decodedToken);
  const canViewList = permissionHas(userPermissionCodes, PermissionCodes.CREDENTIALING_TRACKING_LIST, decodedToken);

  const locationTracking = location?.state?.tracking ?? location?.state ?? null;
  const [trackingData, setTrackingData] = React.useState(locationTracking);
  const [loading, setLoading] = React.useState(locationTracking ? false : true);

  const targetTrackingId = React.useMemo(() => {
    if (!id || id === 'first' || id === ':id') return null;
    return id;
  }, [id]);

  const loadData = React.useCallback(async () => {
    if (!token) {
      return;
    }

    try {
      setLoading(true);
      let resolvedId = targetTrackingId;
      if (!resolvedId) {
        const listRes = await getAllTrackings(token);
        const rawList = listRes?.data?.trackings || listRes?.data?.Trackings || listRes?.data || listRes;
        const list = Array.isArray(rawList) ? rawList : [];
        if (list.length > 0) {
          const firstItem = list[0];
          const firstTrack = firstItem?.tracks?.[0] || firstItem?.Trackings?.[0] || firstItem;
          const pId = firstTrack?.provider_id || firstTrack?.ProviderId || firstItem?.provider?.id;
          const payId = firstTrack?.healthcare_payor_id || firstTrack?.HealthcarePayorId || firstItem?.healthcare_payor?.id;
          if (pId && payId) {
            resolvedId = `${pId}~${payId}`;
          } else if (pId) {
            resolvedId = String(pId);
          }
        }
      }

      if (!resolvedId) {
        setTrackingData(null);
        return;
      }

      const response = await getTrackingById(resolvedId, token);
      if (response?.is_success) {
        setTrackingData(normalizeTrackingPayload(response));
      } else if (response?.status_code === 500) {
        toast.error('Internal Server Error. Please Contact Support Team.');
        setTrackingData(null);
      } else {
        toast.error(response?.message || 'Failed to load monitoring details.');
        setTrackingData(null);
      }
    } catch (error) {
      console.error(error);
      setTrackingData(null);
    } finally {
      setLoading(false);
    }
  }, [targetTrackingId, token]);

  React.useEffect(() => {
    if (!locationTracking) {
      loadData();
    }
  }, [locationTracking, loadData]);

  const tracking = React.useMemo(() => normalizeTrackingPayload(trackingData), [trackingData]);
  const provider = tracking?.provider || {};
  const payor = tracking?.payor || {};
  const tracks = tracking?.tracks || [];
  const firstTrack = tracks[0] || null;

  const providerName = tracking?.providerName || formatDisplayName(provider);
  const payorName = tracking?.payorName || payor?.payor_name || 'Unknown Payor';
  const statusName = tracking?.statusName || firstTrack?.status_name || 'Unknown';
  const statusBadgeStyle = STATUS_STYLE[statusName] || STATUS_STYLE.Unknown;
  const contactEmail = firstTrack?.email || provider?.personal_email || '-';
  const contactPhone = firstTrack?.phone || provider?.phone || '-';
  const createdOnLocal = formatDate(tracking?.createdOn || firstTrack?.created_on || firstTrack?.CreatedOn);
  const modifiedOnLocal = formatDate(firstTrack?.modified_on || firstTrack?.ModifiedOn);

  const summaryCards = React.useMemo(() => [
    {
      label: 'Total Trackings',
      value: tracking?.totalTrackings ?? tracks.length,
      icon: <MdListAlt size={20} className="hc-text-primary" />,
      accent: 'bg-primary-subtle',
    },
    {
      label: 'Overdue',
      value: tracking?.overdueTrackings ?? 0,
      icon: <MdWarningAmber size={20} className="text-danger" />,
      accent: 'bg-danger-subtle',
    },
    {
      label: 'Managed By',
      value: tracking?.managedByName || firstTrack?.created_by_name || '-',
      icon: <MdPerson size={20} className="hc-text-primary" />,
      accent: 'bg-light',
    },
  ], [firstTrack?.created_by_name, tracking?.managedByName, tracking?.overdueTrackings, tracking?.totalTrackings, tracks.length]);

  if (loading) {
    return (
      <div className="container-fluid py-4">
        <div className="d-flex flex-wrap gap-2 align-items-center justify-content-between mb-3">
          <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 40, width: 140, borderRadius: 999 }} />
          <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 40, width: 120, borderRadius: 12 }} />
        </div>

        <div className="p-4 p-lg-5 hc-surface mb-4">
          <div className="skeleton mb-3 rounded-pill" style={{ height: 24, width: 220 }} />
          <div className="skeleton mb-2 w-50" style={{ height: 32, borderRadius: 10 }} />
          <div className="skeleton mb-2 rounded-3" style={{ height: 18, width: '38%' }} />
          <div className="skeleton mb-2 rounded-3" style={{ height: 18, width: '28%' }} />
        </div>

        <div className="d-flex flex-wrap gap-4 mb-4">
          {[1, 2, 3].map((item) => (
            <div className="flex-fill" key={item}>
              <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 120, borderRadius: 16 }} />
            </div>
          ))}
        </div>

        <div className="row g-4 mb-4 align-items-stretch">
          <div className="col-12 col-lg-6">
            <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 260, borderRadius: 20 }} />
          </div>
          <div className="col-12 col-lg-6">
            <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 260, borderRadius: 20 }} />
          </div>
        </div>

        <div className="p-4 p-lg-5 hc-surface hc-card-primary">
          <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-4">
            <div className="skeleton position-relative overflow-hidden rounded-2" style={{ width: 180, height: 28, borderRadius: 10 }} />
            <div className="skeleton position-relative overflow-hidden rounded-2" style={{ width: 90, height: 28, borderRadius: 999 }} />
          </div>
          {Array.from({ length: 2 }).map((_, index) => (
            <div key={index} className="mb-3">
              <div className="skeleton position-relative overflow-hidden rounded-2" style={{ height: 150, borderRadius: 18 }} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!tracking) {
    return (
      <ViewEmptyState
        title="No Monitoring Records Available"
        description="There are no credentialing monitoring records available to display."
        icon={MdMonitor}
        canViewList={canViewList}
        backRoute={APPRoutes.CREDENTIALING_TRACKING}
        backLabel="Back to Monitoring"
      />
    );
  }

  if (!canViewTracking) {
    return (
      <div className="container-fluid py-4">
        <div className="alert alert-danger">You do not have permission to view this monitoring detail.</div>
      </div>
    );
  }

  return (
    <div className="container-fluid py-4">
      <div className="d-flex flex-wrap gap-2 align-items-center justify-content-between mb-3">
        {canViewList && (
          <BackButton onClick={() => navigate(APPRoutes.CREDENTIALING_TRACKING)} />
        )}

        {canEditTracking ? (
          <button
            type="button"
            className="btn hc-bg-primary text-white d-inline-flex align-items-center gap-2"
            onClick={() => navigate(APPRoutes.CREDENTIALING_TRACKING_EDIT.replace(':id', id || ''))}
          >
            <MdEdit size={18} /> Edit Monitoring
          </button>
        ) : null}
      </div>

      <div className="p-4 p-lg-5 mb-4 overflow-hidden hc-hero-banner position-relative hc-hero-banner-primary">
        <div aria-hidden="true" className="hc-hero-circle-tr position-absolute rounded-circle pe-none" />
        <div aria-hidden="true" className="hc-hero-circle-bl position-absolute rounded-circle pe-none" />

        <div className="row g-4 align-items-center position-relative">
          <div className="col-lg-8">
            <div className="d-flex flex-wrap align-items-center gap-3 mb-3">
              <span
                className="px-3 py-2 rounded-pill fw-semibold"
                style={{
                  fontSize: '0.82rem',
                  backgroundColor: statusBadgeStyle.backgroundColor,
                  color: statusBadgeStyle.color,
                }}
              >
                {statusName}
              </span>
              <span className="px-3 py-2 rounded-pill" style={{ fontSize: '0.82rem', backgroundColor: 'rgba(var(--hc-white-rgb), 0.12)', color: 'var(--hc-white)' }}>
                {payorName}
              </span>
            </div>

            <h2 className="fw-bold text-white mb-2" style={{ fontSize: 'clamp(1.45rem, 2.2vw, 2rem)' }}>
              {providerName}
            </h2>
            <p className="mb-1" style={{ color: 'rgba(var(--hc-white-rgb), 0.78)', fontSize: '0.95rem' }}>
              <MdEmail size={14} className="me-1" />
              {contactEmail}
            </p>
            <p className="mb-0" style={{ color: 'rgba(var(--hc-white-rgb), 0.65)', fontSize: '0.88rem' }}>
              <MdPhone size={14} className="me-1" />
              {contactPhone}
            </p>
          </div>

          <div className="col-lg-4">
            <div className="p-3 rounded-4" style={{ backgroundColor: 'rgba(var(--hc-white-rgb), 0.10)' }}>
              <div className="d-flex align-items-center gap-3 mb-3">
                <div className="d-flex align-items-center justify-content-center rounded-3 flex-shrink-0" style={{ width: 56, height: 56, background: 'rgba(var(--hc-white-rgb), 0.15)' }}>
                  <MdNotificationsActive size={24} className="text-white" />
                </div>
                <div>
                  <p className="mb-0 small" style={{ color: 'rgba(var(--hc-white-rgb), 0.65)' }}>Monitoring Overview</p>
                  <p className="fw-bold text-white mb-0" style={{ fontSize: '1.55rem', lineHeight: 1.1 }}>
                    {tracking?.totalTrackings ?? tracks.length}
                  </p>
                </div>
              </div>
              <p className="mb-0 small" style={{ color: 'rgba(var(--hc-white-rgb), 0.65)' }}>
                Summary of monitoring follow-ups, overdue items, and the associated healthcare payor.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="d-flex flex-wrap gap-4 mb-4">
        {summaryCards.map((card) => (
          <div className="flex-fill" key={card.label}>
            <div className="p-4 hc-surface hc-card-primary h-100 d-flex align-items-center gap-3">
              <div className={`d-inline-flex align-items-center justify-content-center rounded-3 ${card.accent}`} style={{ width: 44, height: 44 }}>
                {card.icon}
              </div>
              <div className="flex-grow-1">
                <div className="small text-muted">{card.label}</div>
                <div className="fw-bold text-dark mt-1" style={{ fontSize: '1.05rem' }}>
                  {card.value}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="row g-4 mb-4 align-items-stretch">
        <div className="col-lg-6 d-flex">
          <div className="p-4 p-lg-5 hc-surface hc-card-primary w-100 h-100">
            <h5 className="fw-bold mb-4 d-flex align-items-center gap-2 hc-text-primary">
              <MdBusiness size={22} className="hc-text-primary" /> Payor Details
            </h5>
            <div className="row g-3">
              <div className="col-12 col-md-6">
                <div className="p-3 rounded-4 bg-white border h-100">
                  <div className="small fw-semibold text-muted mb-1">Healthcare Payor</div>
                  <div className="fw-semibold text-dark">{payorName}</div>
                </div>
              </div>
              {firstTrack?.tracking_number && (
                <div className="col-12 col-md-6">
                  <div className="p-3 rounded-4 bg-white border h-100">
                    <div className="small fw-semibold text-muted mb-1">Tracking Number</div>
                    <div className="fw-semibold text-dark">{firstTrack.tracking_number}</div>
                  </div>
                </div>
              )}
              <div className="col-12 col-md-6">
                <div className="p-3 rounded-4 bg-white border h-100">
                  <div className="small fw-semibold text-muted mb-1">Category / Plan</div>
                  <div className="fw-semibold text-dark">{formatValue(payor?.category_plan || payor?.categoryPlan || payor?.plan, '-')}</div>
                </div>
              </div>
              <div className="col-12 col-md-6">
                <div className="p-3 rounded-4 bg-white border h-100">
                  <div className="small fw-semibold text-muted mb-1">State</div>
                  <div className="fw-semibold text-dark">{formatValue(payor?.payor_state || payor?.state, '-')}</div>
                </div>
              </div>
              <div className="col-12 col-md-6">
                <div className="p-3 rounded-4 bg-white border h-100">
                  <div className="small fw-semibold text-muted mb-1">Effective Date</div>
                  <div className="fw-semibold text-dark">{formatDate(payor?.effective_date || payor?.effectiveDate)}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-lg-6 d-flex">
          <div className="p-4 p-lg-5 hc-surface hc-card-primary w-100 h-100">
            <h5 className="fw-bold mb-4 d-flex align-items-center gap-2 hc-text-primary">
              <MdCalendarToday size={22} className="hc-text-primary" /> Monitoring Activity
            </h5>
            <div className="row g-3">
              <div className="col-12 col-md-6">
                <div className="p-3 rounded-4 bg-white border h-100">
                  <div className="small fw-semibold text-muted mb-1">Created On</div>
                  <div className="fw-semibold text-dark">{createdOnLocal}</div>
                </div>
              </div>
              <div className="col-12 col-md-6">
                <div className="p-3 rounded-4 bg-white border h-100">
                  <div className="small fw-semibold text-muted mb-1">Modified On</div>
                  <div className="fw-semibold text-dark">{modifiedOnLocal}</div>
                </div>
              </div>
              <div className="col-12">
                <div className="p-3 rounded-4 bg-white border h-100">
                  <div className="small fw-semibold text-muted mb-1">Managed By</div>
                  <div className="fw-semibold text-dark">{tracking?.managedByName || firstTrack?.created_by_name || '-'}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="p-4 p-lg-5 hc-surface hc-card-primary">
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-4">
          <h4 className="fw-bold mb-0 d-flex align-items-center gap-2 hc-text-primary">
            <MdSchedule size={24} className="hc-text-primary" /> Tracking Items
          </h4>
          <span className="badge rounded-pill px-3 py-2 bg-primary-subtle hc-text-primary fw-semibold">
            {tracks.length} item{tracks.length === 1 ? '' : 's'}
          </span>
        </div>

        {tracks.length ? (
          <div className="d-flex flex-column gap-3">
            {tracks.map((track) => {
              const isOverdue = Number(track?.overdue_days || 0) > 0;
              const trackStatusName = (track?.status_name || statusName);
              const trackStatusStyle = STATUS_STYLE[trackStatusName] || STATUS_STYLE.Unknown;
              const modeOfCom = (track?.mode_of_com || track?.ModeOfCom || '').toString().toLowerCase();
              const contactValue = modeOfCom === 'phone'
                ? formatValue(track?.phone || track?.Phone || contactPhone, '-')
                : formatValue(track?.email || track?.Email || contactEmail, '-');

              return (
                <div className="border rounded-4 p-4 bg-white" key={track?.id || `${track?.follow_up_id}-${track?.est_date_time}`}>
                  <div className="d-flex flex-wrap align-items-start justify-content-between gap-3 mb-3">
                    <div>
                      <div className="d-flex flex-wrap align-items-center gap-2 mb-1">
                        <h6 className="fw-bold mb-0">{formatValue(track?.follow_up_name || 'Follow-up', 'Follow-up')}</h6>
                        <span className="badge rounded-pill px-3 py-2 bg-info bg-gradient text-white fw-semibold">
                          {formatValue(track?.assignee_name || track?.assigneeName || track?.assignee || 'Unassigned', 'Unassigned')}
                        </span>
                      </div>
                      <p className="mb-0 small text-muted">
                        Created {formatDate(track?.created_on || track?.CreatedOn)}
                      </p>
                    </div>
                    <div className="d-flex flex-wrap gap-2">
                      <span className="badge rounded-pill px-3 py-2 fw-semibold" style={trackStatusStyle}>
                        {trackStatusName}
                      </span>
                      {isOverdue ? (
                        <span className="badge rounded-pill px-3 py-2 fw-semibold d-inline-flex align-items-center gap-1" style={{ backgroundColor: 'var(--hc-status-danger-bg)', color: 'var(--hc-status-danger-text)' }}>
                          <MdWarningAmber size={14} />
                          Overdue {track.overdue_days} day{Number(track.overdue_days) === 1 ? '' : 's'}
                        </span>
                      ) : null}
                    </div>
                  </div>

                  <div className="row g-3">
                    <div className="col-12 col-md-6 col-xl">
                      <div className="rounded-3 p-3 bg-light border">
                        <div className="small fw-semibold text-muted mb-1">Estimated Date</div>
                        <div className="fw-semibold text-dark">{formatDate(track?.est_date_time || track?.EstDateTime)}</div>
                      </div>
                    </div>
                    <div className="col-12 col-md-6 col-xl">
                      <div className="rounded-3 p-3 bg-light border">
                        <div className="small fw-semibold text-muted mb-1">Mode</div>
                        <div className="fw-semibold text-dark">{formatValue(track?.mode_of_com || track?.ModeOfCom, '-')}</div>
                      </div>
                    </div>
                    <div className="col-12 col-md-6 col-xl">
                      <div className="rounded-3 p-3 bg-light border">
                        <div className="small fw-semibold text-muted mb-1">Submission Mode</div>
                        <div className="fw-semibold text-dark">{formatValue(track?.submission_mode || track?.SubmissionMode, '-')}</div>
                      </div>
                    </div>
                    <div className="col-12 col-md-6 col-xl">
                      <div className="rounded-3 p-3 bg-light border">
                        <div className="small fw-semibold text-muted mb-1">Monitoring Completed</div>
                        <div className="fw-semibold text-dark d-flex align-items-center gap-2">
                          {track?.is_monitoring_completed ? <MdCheckCircle className="text-success" size={16} /> : <MdWarningAmber className="text-warning" size={16} />}
                          {track?.is_monitoring_completed ? 'Yes' : 'No'}
                        </div>
                      </div>
                    </div>
                    <div className="col-12 col-md-6 col-xl">
                      <div className="rounded-3 p-3 bg-light border h-100">
                        <div className="small fw-semibold text-muted mb-1">Contact</div>
                        <div className="fw-semibold text-dark">{contactValue}</div>
                      </div>
                    </div>
                  </div>

                  <div className="mt-3 rounded-3 p-3 bg-light border">
                    <div className="small fw-semibold text-muted mb-1">Notes</div>
                    <div className="text-dark">{formatValue(track?.notes || 'No notes provided.', 'No notes provided.')}</div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="mb-0 text-muted">No tracking items are available for this monitoring record.</p>
        )}
      </div>
    </div>
  );
}

